import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '../lib/api.ts';
import { useConfirm } from '../lib/confirm.tsx';
import { Avatar } from '../components/Avatar.tsx';
import { Btn } from '../components/Btn.tsx';
import { NavIcon } from '../components/NavIcon.tsx';
import { CenteredSpinner, Spinner } from '../components/Spinner.tsx';
import { EmptyState } from '../components/EmptyState.tsx';
import { Modal } from '../components/Modal.tsx';
import { Field, Select, TextInput } from '../components/Field.tsx';
import { ErrorMessage } from '../components/ErrorMessage.tsx';
import { SearchableMultiSelect } from '../components/SearchableMultiSelect.tsx';
import { listSummary, mergedOptions } from '../lib/adminAccess.ts';
import { contractorTypeOptions, stationOptions } from '../lib/companyInfo.ts';
import { usePortalSettings } from '../lib/portalSettings.tsx';
import { useAdminAuth } from '../lib/auth/admin.tsx';
import type { AdminUserListItem } from '../lib/types.ts';

type Role = 'admin' | 'reviewer';

const ROLE_VALUES: Role[] = ['admin', 'reviewer'];
const roleLabel = (r: string): string => (r === 'admin' ? 'Admin' : 'Reviewer');

type ScopePatch = {
  assigned_stations?: string[] | null;
  assigned_contractor_types?: string[] | null;
};

// One inline scope column. Admins get an editable MultiSelect; everyone else
// sees a read-only text summary. Empty selection ("All …") = unrestricted.
function ScopeCell({
  assigned,
  noun,
  options,
  editable,
  onChange,
}: {
  assigned: string[] | null;
  noun: string; // e.g. 'stations' | 'contractor types'
  options: string[];
  editable: boolean;
  onChange: (next: string[]) => void;
}) {
  if (!editable) {
    return <span className="text-xs text-slate-600">{listSummary(assigned, noun)}</span>;
  }
  const selected = assigned ?? [];
  const capNoun = noun.charAt(0).toUpperCase() + noun.slice(1);
  return (
    <SearchableMultiSelect
      label={selected.length ? capNoun : `All ${noun}`}
      ariaLabel={`Assign ${noun}`}
      unitLabel={noun}
      values={options}
      selected={selected}
      onChange={onChange}
    />
  );
}

export function AdminUsers() {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const { user: currentUser, reload } = useAdminAuth();
  const isAdmin = currentUser?.role === 'admin';
  const { settings } = usePortalSettings();

  const { data, isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: () => adminApi.get<{ users: AdminUserListItem[] }>('/api/users'),
  });

  const [roleFilter, setRoleFilter] = useState<Role[]>([]);
  const [search, setSearch] = useState('');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteRole, setInviteRole] = useState<Role>('admin');
  const [inviteError, setInviteError] = useState<unknown>(null);

  const inviteMutation = useMutation({
    mutationFn: (body: { email: string; name: string; role: Role }) =>
      adminApi.post<{ user: AdminUserListItem }>('/api/users', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] });
      closeInvite();
    },
    onError: (err) => setInviteError(err),
  });

  function closeInvite() {
    setInviteOpen(false);
    setInviteEmail('');
    setInviteName('');
    setInviteRole('admin');
    setInviteError(null);
  }

  const updateMutation = useMutation({
    mutationFn: (input: {
      id: string;
      patch: { role?: Role; name?: string } & ScopePatch;
    }) => adminApi.patch(`/api/users/${input.id}`, input.patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminApi.delete(`/api/users/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });

  function saveScope(u: AdminUserListItem, patch: ScopePatch) {
    updateMutation.mutate(
      { id: u.id, patch },
      {
        onSuccess: () => {
          // Own scope changed → refresh sidebar + drop now-out-of-scope cached rows.
          if (u.id === currentUser?.id) {
            void reload();
            void qc.invalidateQueries();
          }
        },
      },
    );
  }

  if (isLoading) return <CenteredSpinner />;
  const users = data?.users ?? [];
  const term = search.trim().toLowerCase();
  const visibleUsers = users.filter((u) => {
    if (roleFilter.length && !roleFilter.includes(u.role)) return false;
    if (term && !(u.name.toLowerCase().includes(term) || u.email.toLowerCase().includes(term)))
      return false;
    return true;
  });

  function onInviteSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    inviteMutation.mutate({
      email: inviteEmail.trim(),
      name: inviteName.trim(),
      role: inviteRole,
    });
  }

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Users</h1>
          <p className="mt-1 text-sm text-slate-500">
            Manage admin and reviewer access to the portal.
          </p>
        </div>
        {isAdmin && <Btn onClick={() => setInviteOpen(true)}>Invite user</Btn>}
      </div>

      {users.length === 0 ? (
        <EmptyState title="No users yet" description="Invite your first admin to get started." />
      ) : (
        <>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="sm:w-72">
              <Field label="Search">
                <TextInput
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by name or email"
                />
              </Field>
            </div>
            <SearchableMultiSelect
              label="Role"
              unitLabel="roles"
              values={ROLE_VALUES}
              labelFor={roleLabel}
              selected={roleFilter}
              onChange={(next) => setRoleFilter(next as Role[])}
            />
          </div>
          <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Role</th>
                  <th className="px-4 py-3">Station</th>
                  <th className="px-4 py-3">Contractor type</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {visibleUsers.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-sm text-slate-500">
                      No users match your search.
                    </td>
                  </tr>
                ) : (
                  visibleUsers.map((u) => {
                    const isSelf = u.id === currentUser?.id;
                    return (
                      <tr key={u.id}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <Avatar name={u.name} size={32} />
                            <span className="font-medium text-slate-900">{u.name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-slate-700">{u.email}</td>
                        <td className="px-4 py-3">
                          {isAdmin && !isSelf ? (
                            <Select
                              value={u.role}
                              onChange={(e) =>
                                updateMutation.mutate({
                                  id: u.id,
                                  patch: { role: e.target.value as Role },
                                })
                              }
                            >
                              <option value="admin">Admin</option>
                              <option value="reviewer">Reviewer</option>
                            </Select>
                          ) : (
                            <span className="inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium capitalize text-slate-700">
                              {u.role}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <ScopeCell
                            assigned={u.assigned_stations}
                            noun="stations"
                            options={mergedOptions(stationOptions(settings), u.assigned_stations)}
                            editable={isAdmin}
                            onChange={(next) => saveScope(u, { assigned_stations: next })}
                          />
                        </td>
                        <td className="px-4 py-3">
                          <ScopeCell
                            assigned={u.assigned_contractor_types}
                            noun="contractor types"
                            options={mergedOptions(
                              contractorTypeOptions(settings),
                              u.assigned_contractor_types,
                            )}
                            editable={isAdmin}
                            onChange={(next) => saveScope(u, { assigned_contractor_types: next })}
                          />
                        </td>
                        <td className="px-4 py-3 text-right">
                          {isAdmin && !isSelf ? (
                            <button
                              type="button"
                              aria-label={`Remove ${u.name}`}
                              title="Remove user"
                              disabled={
                                deleteMutation.isPending && deleteMutation.variables === u.id
                              }
                              onClick={() => {
                                void (async () => {
                                  if (
                                    await confirm({
                                      title: 'Remove user',
                                      body: (
                                        <>
                                          Are you sure you want to remove{' '}
                                          <span className="font-semibold">{u.email}</span>?
                                          They&apos;ll lose access immediately.
                                        </>
                                      ),
                                      confirmLabel: 'Remove',
                                      danger: true,
                                    })
                                  )
                                    deleteMutation.mutate(u.id);
                                })();
                              }}
                              className="inline-flex items-center justify-center rounded-md p-1.5 text-red-600 transition hover:bg-red-50 hover:text-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              {deleteMutation.isPending && deleteMutation.variables === u.id ? (
                                <Spinner size={16} />
                              ) : (
                                <NavIcon name="trash" size={16} />
                              )}
                            </button>
                          ) : (
                            <span className="text-xs text-slate-400">
                              {isSelf ? 'You' : 'Read-only'}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      <Modal
        open={inviteOpen}
        onClose={closeInvite}
        title="Invite user"
        footer={
          <>
            <Btn variant="secondary" onClick={closeInvite}>
              Cancel
            </Btn>
            <Btn
              type="submit"
              form="invite-form"
              loading={inviteMutation.isPending}
              disabled={!inviteEmail.trim() || !inviteName.trim()}
            >
              Send invitation
            </Btn>
          </>
        }
      >
        <form id="invite-form" className="space-y-4" onSubmit={onInviteSubmit}>
          <Field label="Email" required>
            <TextInput
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              required
            />
          </Field>
          <Field label="Name" required>
            <TextInput
              value={inviteName}
              onChange={(e) => setInviteName(e.target.value)}
              required
            />
          </Field>
          <Field label="Role">
            <Select value={inviteRole} onChange={(e) => setInviteRole(e.target.value as Role)}>
              <option value="admin">Admin</option>
              <option value="reviewer">Reviewer</option>
            </Select>
          </Field>
          <p className="text-xs text-slate-500">
            An invitation email will be sent. The user sets their password from the link, then signs
            in with their email and password.
          </p>
          <ErrorMessage error={inviteError} />
        </form>
      </Modal>
    </div>
  );
}
